import CachedIcon from '@mui/icons-material/Cached';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Checkbox, FormControlLabel, Stack, styled, Typography } from '@mui/material';
import { TooltipCC } from 'components/common';
import { useTranslation } from 'react-i18next';
import { color } from 'theme';
import { NoticeEndRoom } from './NoticeEndRoom';
import { CAMERA, CHANGE_NAME, HAND_UP, MICROPHONE, POLL, RECORD, SHARING_FILE, SHARING_SCREEN, WHITEBOARD } from '../constants';
import { MESSAGE } from 'constants/roles';

const InfoOutlinedIconCC = styled(InfoOutlinedIcon)({
    fontSize: 18,
    color: color.grey01,
});

export const AdvancedSettings = ({ data, setData, pageDetail = false }) => {
    const { t } = useTranslation();

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Stack>
                <Box sx={{ paddingLeft: '6px', paddingBottom: '15px' }}>
                    <Typography fontWeight={700} color={(theme) => theme.palette.text[700]}>
                        {t('room.settings.permission-detail')}:
                    </Typography>
                </Box>
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={t('room.settings.micro-permission-detail')}
                    control={
                        <Checkbox
                            disabled={pageDetail}
                            size="small"
                            tabIndex={0}
                            checked={data?.micro === MICROPHONE.ON ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        micro: MICROPHONE.ON,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        micro: MICROPHONE.OFF,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        micro: data?.micro === MICROPHONE.ON ? MICROPHONE.OFF : MICROPHONE.ON,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={t('room.settings.camera-permission-detail')}
                    control={
                        <Checkbox
                            disabled={pageDetail}
                            size="small"
                            tabIndex={0}
                            checked={data?.camera === CAMERA.ON ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        camera: CAMERA.ON,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        camera: CAMERA.OFF,
                                        sharingScreenPermission: SHARING_FILE.OFF,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        camera: data?.camera === CAMERA.ON ? CAMERA.OFF : CAMERA.ON,
                                        sharingScreenPermission:
                                            data?.camera === CAMERA.ON
                                                ? SHARING_SCREEN.OFF
                                                : data?.sharingScreenPermission,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={
                        <Box display={'flex'}>
                            <Typography fontWeight={500}>
                                {t('room.settings.sharing-screen-permission-detail')}
                            </Typography>{' '}
                            <TooltipCC
                                placement="top-start"
                                title={t('room.settings.content-sharing-screen-permission')}
                            >
                                <InfoOutlinedIconCC />
                            </TooltipCC>
                        </Box>
                    }
                    control={
                        <Checkbox
                            defaultChecked
                            size="small"
                            tabIndex={0}
                            disabled={data?.camera === CAMERA.OFF || pageDetail ? true : false}
                            checked={data?.sharingScreenPermission === SHARING_SCREEN.ON ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        sharingScreenPermission: SHARING_SCREEN.ON,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        sharingScreenPermission: SHARING_SCREEN.OFF,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        sharingScreenPermission:
                                            data?.sharingScreenPermission === SHARING_SCREEN.ON
                                                ? SHARING_SCREEN.OFF
                                                : SHARING_SCREEN.ON,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={t('room.settings.white-board-permission-detail')}
                    control={
                        <Checkbox
                            disabled={pageDetail}
                            size="small"
                            tabIndex={0}
                            checked={data?.whiteboardPermission === WHITEBOARD.ON ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        whiteboardPermission: WHITEBOARD.ON,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        whiteboardPermission: WHITEBOARD.OFF,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        whiteboardPermission:
                                            data?.whiteboardPermission === WHITEBOARD.ON
                                                ? WHITEBOARD.OFF
                                                : WHITEBOARD.ON,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={
                        <Box display={'flex'}>
                            <Typography fontWeight={500}>
                                {t('room.settings.message-group-permission-detail')}
                            </Typography>{' '}
                            <TooltipCC
                                placement="top-start"
                                title={t('room.settings.content-message-group-permission')}
                            >
                                <InfoOutlinedIconCC />
                            </TooltipCC>
                        </Box>
                    }
                    control={
                        <Checkbox
                            disabled={pageDetail}
                            defaultChecked
                            size="small"
                            tabIndex={0}
                            checked={data?.message === MESSAGE.ON ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        message: MESSAGE.ON,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        message: MESSAGE.OFF,
                                        sharingFile: SHARING_FILE.OFF,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        message: data?.message === MESSAGE.ON ? MESSAGE.OFF : MESSAGE.ON,
                                        sharingFile:
                                            data?.message === MESSAGE.ON ? SHARING_FILE.OFF : data?.sharingFile,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={
                        <Box display={'flex'}>
                            <Typography fontWeight={500}>
                                {t('room.settings.sharing-file-permission-detail')}
                            </Typography>{' '}
                            <TooltipCC placement="top-start" title={t('room.settings.content-sharing-file-permission')}>
                                <InfoOutlinedIconCC />
                            </TooltipCC>
                        </Box>
                    }
                    control={
                        <Checkbox
                            defaultChecked
                            size="small"
                            tabIndex={0}
                            disabled={data?.message === MESSAGE.OFF || pageDetail ? true : false}
                            checked={data?.sharingFile === SHARING_FILE.ON ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        sharingFile: SHARING_FILE.ON,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        sharingFile: SHARING_FILE.OFF,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        sharingFile:
                                            data?.sharingFile === SHARING_FILE.ON ? SHARING_FILE.OFF : SHARING_FILE.ON,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={t('room.settings.hand-up-permission-detail')}
                    control={
                        <Checkbox
                            disabled={pageDetail}
                            defaultChecked
                            size="small"
                            tabIndex={0}
                            checked={data?.handUpPermission === HAND_UP.ON ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        handUpPermission: SHARING_FILE.ON,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        handUpPermission: SHARING_FILE.OFF,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        handUpPermission:
                                            data?.handUpPermission === HAND_UP.ON ? HAND_UP.OFF : HAND_UP.ON,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={t('room.settings.poll-permission-detail')}
                    control={
                        <Checkbox
                            disabled={pageDetail}
                            defaultChecked
                            size="small"
                            tabIndex={0}
                            checked={data?.pollPermission === POLL.EVERYONE ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        pollPermission: POLL.EVERYONE,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        pollPermission: POLL.HOST_AND_COHOST,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        pollPermission:
                                            data?.pollPermission === POLL.EVERYONE
                                                ? POLL.HOST_AND_COHOST
                                                : POLL.EVERYONE,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={t('room.settings.change-name-permission-detail')}
                    control={
                        <Checkbox
                            disabled={pageDetail}
                            defaultChecked
                            size="small"
                            tabIndex={0}
                            checked={data?.changeNamePermission === CHANGE_NAME.ON ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        changeNamePermission: CHANGE_NAME.ON,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        changeNamePermission: CHANGE_NAME.OFF,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        changeNamePermission:
                                            data?.changeNamePermission === CHANGE_NAME.ON
                                                ? CHANGE_NAME.OFF
                                                : CHANGE_NAME.ON,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <FormControlLabel
                    sx={{ paddingLeft: 1.25 }}
                    labelPlacement="end"
                    label={t('room.settings.record-permission-detail')}
                    control={
                        <Checkbox
                            disabled={pageDetail}
                            defaultChecked
                            size="small"
                            tabIndex={0}
                            checked={data?.recordPermission === RECORD.ON ? true : false}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setData((prevData) => ({
                                        ...prevData,
                                        recordPermission: RECORD.ON,
                                    }));
                                } else {
                                    setData((prevData) => ({
                                        ...prevData,
                                        recordPermission: RECORD.OFF,
                                    }));
                                }
                            }}
                            onKeyUp={(e) => {
                                if (e.code === 'Enter') {
                                    e.preventDefault();
                                    setData((prevData) => ({
                                        ...prevData,
                                        recordPermission: data?.recordPermission === RECORD.ON ? RECORD.OFF : RECORD.ON,
                                    }));
                                }
                            }}
                        />
                    }
                />
                <div className="col-md-12" style={{ display: pageDetail ? 'none' : 'flex', alignItemss: 'center' }}>
                    <Button
                        startIcon={<CachedIcon sx={{ color: '#CB002B', fontWeight: '700' }} />}
                        variant="outlined"
                        onClick={() =>
                            setData((prevData) => ({
                                ...prevData,
                                camera: CAMERA.ON,
                                micro: MICROPHONE.ON,
                                message: MESSAGE.ON,
                                sharingFile: SHARING_FILE.ON,
                                pollPermission: POLL.HOST_AND_COHOST,
                                sharingScreenPermission: SHARING_SCREEN.ON,
                                whiteboardPermission: WHITEBOARD.ON,
                                handUpPermission: HAND_UP.ON,
                                changeNamePermission: CHANGE_NAME.ON,
                                recordPermission: RECORD.OFF,
                            }))
                        }
                        sx={{ marginRight: '10px', textAlign: 'justify', mt: 1, ml: 1 }}
                    >
                        <Box sx={{ fontWeight: '600' }}>{t('room.settings.title-default-settings')}</Box>
                    </Button>
                    <TooltipCC placement="top-start" title={t('room.settings.content-default-settings')}>
                        <WarningAmberIcon sx={{ fontSize: 18, color: '#f59b42' }} />
                    </TooltipCC>
                </div>
            </Stack>
            <Box></Box>
            <NoticeEndRoom t={t} data={data} setData={setData} pageDetail={pageDetail} />
            {/* <Box sx={{display: 'flex', alignItems: 'center'}}>
                <Checkbox sx={{color: '#ee0033'}} defaultChecked size="small" />
                <Box>Cho phép mọi người tự do trò chuyện</Box>
            </Box>
            <Box sx={{display: 'flex'}}>
                <Checkbox sx={{color: '#ee0033'}} defaultChecked size="small" />
                <Box>Người tham gia không thể trò chuyện trong nhóm chung</Box>
            </Box>
            
            <Box sx={{display: 'flex'}}>
                <Checkbox sx={{color: '#ee0033'}} defaultChecked size="small" />
                <Box>Cho phép mọi người gửi tài liệu trong cuộc họp</Box>
            </Box>
            <Box sx={{display: 'flex'}}>
                <Checkbox sx={{color: '#ee0033'}} defaultChecked size="small" />
                <Box>Người tham gia không thể gửi tài liệu trong nhóm chung</Box>
            </Box>
            
            <Box sx={{display: 'flex'}}>
                <Checkbox sx={{color: '#ee0033'}} defaultChecked size="small" />
                <Box>Mọi người tham gia đều có thể tạo bình chọn</Box>
            </Box>
            <Box sx={{display: 'flex'}}>
                <Checkbox sx={{color: '#ee0033'}} defaultChecked size="small" />
                <Box>Chỉ chủ trì và đồng chủ trì mới có thể tạo bình chọn</Box>
            </Box> */}
        </Box>
    );
};
